import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';
import {
  TrendingUp, Users, BookOpen, CalendarCheck, DollarSign,
  Printer, ChevronRight, Award, AlertCircle, BarChart2, RefreshCw,
  GraduationCap, CheckCircle, Clock, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

/* ── Shared Helpers ─────────────────────────────────────────────────── */
const fmt = (n) => n?.toLocaleString('en-NG') ?? '—';
const fmtNaira = (n) => `₦${(n || 0).toLocaleString('en-NG')}`;

const DarkTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-xl shadow-xl text-sm" style={{ backgroundColor: '#0F172A' }}>
      <p className="text-slate-400 text-xs mb-0.5">{label}</p>
      <p className="font-semibold text-white">{fmt(payload[0]?.value)}{suffix}</p>
    </div>
  );
};

const MultiTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-xl shadow-xl text-sm" style={{ backgroundColor: '#0F172A' }}>
      <p className="text-slate-400 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold" style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

const EmptyChart = ({ icon: Icon, message }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    <Icon className="w-8 h-8 mb-2" style={{ color: '#E2E8F0' }} strokeWidth={1.5} />
    <p className="text-sm" style={{ color: '#94A3B8' }}>{message}</p>
  </div>
);

const Skeleton = ({ h = 'h-32' }) => (
  <div className={`${h} bg-slate-100 rounded-xl animate-pulse`} />
);

/* ── Section card wrapper ────────────────────────────────────────────── */
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl overflow-hidden ${className}`}
    style={{ border: '1px solid #E2E8F0' }}>
    {children}
  </div>
);

const CardHeader = ({ title, sub }) => (
  <div className="px-6 py-4" style={{ borderBottom: '1px solid #F1F5F9' }}>
    <p className="font-semibold text-slate-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</p>
    {sub && <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{sub}</p>}
  </div>
);

/* ── KPI Stat card ───────────────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div className="bg-white rounded-2xl p-5"
      style={{ border: '1px solid #E2E8F0', transition: 'box-shadow 0.2s ease' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: '0.18em', color: '#94A3B8' }}>{label}</p>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
          <Icon className="w-4 h-4" strokeWidth={1.75} style={{ color: accent }} />
        </div>
      </div>
      <p className="mt-2 text-3xl font-light tracking-tighter" style={{ color: '#0F172A', fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      <p className="mt-1 text-xs" style={{ color: '#94A3B8' }}>{sub}</p>
    </div>
  );
}

/* ── OVERVIEW TAB ─────────────────────────────────────────────────────── */
function OverviewTab({ dash, analytics }) {
  const genderData = (analytics?.students_by_gender || []).map(g => ({ name: g._id || 'Unknown', value: g.count }));
  const GENDER_COLORS = { Male: '#2563EB', Female: '#EC4899', Other: '#F59E0B' };

  const classByStudents = (analytics?.students_by_class || [])
    .map(c => ({ class_name: c._id, count: c.count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const stats = dash?.stats || {};

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Students"   value={fmt(stats.total_students)}   sub="Active enrolment"         icon={Users}          accent="#2563EB" testId="kpi-students" />
        <KpiCard label="Total Teachers"   value={fmt(stats.total_teachers)}   sub="Active staff"             icon={GraduationCap}  accent="#10B981" testId="kpi-teachers" />
        <KpiCard label="Today Present"    value={fmt(stats.today_present)}    sub={`${stats.today_absent || 0} absent today`} icon={CalendarCheck} accent="#8B5CF6" testId="kpi-present" />
        <KpiCard label="Pending Invoices" value={fmt(stats.pending_invoices)} sub="Unpaid fee invoices"      icon={DollarSign}     accent="#F59E0B" testId="kpi-invoices" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Attendance Trend */}
        <Card>
          <CardHeader title="7-Day Attendance Trend" sub="School-wide present vs absent" />
          {(dash?.attendance_trend || []).length === 0 ? (
            <EmptyChart icon={CalendarCheck} message="No attendance recorded yet" />
          ) : (
            <div className="p-5">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dash.attendance_trend} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="presentGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<MultiTooltip />} />
                  <Area type="monotone" dataKey="present" name="Present" stroke="#2563EB" strokeWidth={2.5} fill="url(#presentGrad)" dot={false} />
                  <Line type="monotone" dataKey="absent"  name="Absent"  stroke="#EF4444" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Students by class */}
        <Card>
          <CardHeader title="Students by Class" sub="Active enrolment per class" />
          {classByStudents.length === 0 ? (
            <EmptyChart icon={Users} message="No class data yet" />
          ) : (
            <div className="p-5">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={classByStudents} barSize={18} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                  <XAxis dataKey="class_name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<DarkTooltip />} cursor={{ fill: 'rgba(37,99,235,0.04)' }} />
                  <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Gender distribution */}
      {genderData.length > 0 && (
        <Card>
          <CardHeader title="Gender Distribution" sub="School-wide gender breakdown" />
          <div className="p-5 flex items-center gap-8">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={genderData} cx="50%" cy="50%" outerRadius={60} innerRadius={32} dataKey="value" paddingAngle={3}>
                  {genderData.map((entry, i) => (
                    <Cell key={i} fill={GENDER_COLORS[entry.name] || '#94A3B8'} />
                  ))}
                </Pie>
                <Tooltip content={<DarkTooltip />} />
                <Legend formatter={(v) => <span className="text-xs text-slate-600">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── ACADEMIC PERFORMANCE TAB ────────────────────────────────────────── */
function AcademicTab({ classes, terms }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [selClass, setSelClass] = useState('');
  const [selTerm,  setSelTerm]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selClass) params.set('class_id', selClass);
      if (selTerm)  params.set('term_id',  selTerm);
      const r = await api.get(`/exams/analytics?${params}`);
      setData(r.data || {});
    } catch { toast.error('Failed to load academic data'); }
    finally { setLoading(false); }
  }, [selClass, selTerm]);

  useEffect(() => { load(); }, [load]);

  const classOverview   = data?.class_overview   || [];
  const subjectBreakdown = data?.subject_breakdown || [];
  const termTrend        = data?.term_trend        || [];

  return (
    <div className="space-y-6" data-testid="academic-tab">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select value={selTerm} onChange={e => setSelTerm(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-300"
          data-testid="academic-term-filter">
          <option value="">All Terms</option>
          {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={selClass} onChange={e => setSelClass(e.target.value)}
          className="h-9 px-3 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-blue-300"
          data-testid="academic-class-filter">
          <option value="">All Classes (overview)</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 h-9 px-3 text-xs font-semibold border border-slate-200 rounded-xl bg-white text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-40"
          data-testid="academic-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-4"><Skeleton h="h-48" /><Skeleton h="h-48" /></div>
      ) : (
        <>
          {/* Class Performance Overview */}
          <Card>
            <CardHeader title="Class Performance Overview" sub="Average score and pass rate per class" />
            {classOverview.length === 0 ? (
              <EmptyChart icon={TrendingUp} message="No exam scores recorded yet" />
            ) : (
              <div className="p-5">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={classOverview} barSize={20} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="class_name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<MultiTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="avg_score"  name="Avg Score"  fill="#2563EB" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pass_rate"  name="Pass Rate"  fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {/* Class overview table */}
          {classOverview.length > 0 && (
            <Card>
              <CardHeader title="Class Summary Table" sub="Per-class averages" />
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="class-summary-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #F1F5F9' }}>
                      {['Class', 'Students', 'Avg Score', 'Pass Rate', 'Subjects'].map(h => (
                        <th key={h} className="text-left px-5 py-3 text-[10px] font-bold uppercase text-slate-500" style={{ letterSpacing: '0.12em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {classOverview.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #F8FAFC' }}
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`class-row-${row.class_id}`}>
                        <td className="px-5 py-3 font-semibold text-slate-800">{row.class_name}</td>
                        <td className="px-5 py-3 text-slate-600">{row.student_count}</td>
                        <td className="px-5 py-3">
                          <span className={`font-bold ${row.avg_score >= 60 ? 'text-emerald-600' : row.avg_score >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                            {row.avg_score}%
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full max-w-[80px]">
                              <div className="h-full rounded-full" style={{ width: `${row.pass_rate}%`, backgroundColor: row.pass_rate >= 60 ? '#10B981' : '#F59E0B' }} />
                            </div>
                            <span className="text-xs font-semibold text-slate-600">{row.pass_rate}%</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-500">{row.subject_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Subject breakdown for selected class */}
          {selClass && subjectBreakdown.length > 0 && (
            <Card>
              <CardHeader title="Subject Breakdown" sub={`${classes.find(c => c.id === selClass)?.name || ''} — per subject averages`} />
              <div className="p-5">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={subjectBreakdown} barSize={20} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="subject_name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<MultiTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="avg_score" name="Avg Score" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pass_rate" name="Pass Rate" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Term trend for selected class */}
          {selClass && termTrend.length > 0 && (
            <Card>
              <CardHeader title="Term Trend" sub="Average score per term for selected class" />
              <div className="p-5">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={termTrend} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                    <XAxis dataKey="term_name" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DarkTooltip suffix="%" />} />
                    <Line type="monotone" dataKey="avg_score" stroke="#2563EB" strokeWidth={2.5} dot={{ fill: '#2563EB', r: 4, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/* ── ATTENDANCE REPORT TAB ───────────────────────────────────────────── */
function AttendanceTab({ dash }) {
  const trend = dash?.attendance_trend || [];
  const stats = dash?.stats || {};
  const total = (stats.today_present || 0) + (stats.today_absent || 0);
  const todayRate = total > 0 ? Math.round((stats.today_present / total) * 100) : null;

  const weekStats = trend.reduce((acc, d) => ({
    present: acc.present + (d.present || 0),
    absent:  acc.absent  + (d.absent  || 0),
  }), { present: 0, absent: 0 });
  const weekTotal = weekStats.present + weekStats.absent;
  const weekRate  = weekTotal > 0 ? Math.round((weekStats.present / weekTotal) * 100) : null;

  return (
    <div className="space-y-6" data-testid="attendance-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Today Present"    value={fmt(stats.today_present)} sub={todayRate != null ? `${todayRate}% rate today` : 'Not marked'}      icon={CheckCircle}  accent="#10B981" testId="att-today-present" />
        <KpiCard label="Today Absent"     value={fmt(stats.today_absent)}  sub="Marked absent"                                                         icon={AlertCircle}  accent="#EF4444" testId="att-today-absent" />
        <KpiCard label="Week Attendance"  value={weekRate != null ? `${weekRate}%` : '—'} sub={`${weekStats.present} of ${weekTotal} this week`} icon={CalendarCheck} accent="#2563EB" testId="att-week-rate" />
        <KpiCard label="Total Students"   value={fmt(stats.total_students)} sub="Active enrolment"                                                     icon={Users}        accent="#8B5CF6" testId="att-total-students" />
      </div>

      {/* Trend chart */}
      <Card>
        <CardHeader title="7-Day Attendance Trend" sub="Present and absent counts per day" />
        {trend.length === 0 ? (
          <EmptyChart icon={CalendarCheck} message="No attendance data recorded yet" />
        ) : (
          <div className="p-5">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trend} barSize={20} barGap={4} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<MultiTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="present" name="Present" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="absent"  name="Absent"  fill="#EF4444" radius={[4, 4, 0, 0]} opacity={0.75} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Daily log */}
      {trend.length > 0 && (
        <Card>
          <CardHeader title="Daily Breakdown" sub="Present, absent and attendance rate per day" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #F1F5F9' }}>
                  {['Day', 'Present', 'Absent', 'Total', 'Rate'].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-[10px] font-bold uppercase text-slate-500" style={{ letterSpacing: '0.12em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {trend.map((row, i) => {
                  const total = (row.present || 0) + (row.absent || 0);
                  const rate  = total > 0 ? Math.round(row.present / total * 100) : null;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #F8FAFC' }} className="hover:bg-slate-50"
                      data-testid={`att-row-${row.day}`}>
                      <td className="px-5 py-3 font-semibold text-slate-800">{row.day}</td>
                      <td className="px-5 py-3 text-emerald-600 font-semibold">{row.present || 0}</td>
                      <td className="px-5 py-3 text-red-500 font-semibold">{row.absent || 0}</td>
                      <td className="px-5 py-3 text-slate-500">{total}</td>
                      <td className="px-5 py-3">
                        {rate != null ? (
                          <span className={`font-bold text-sm ${rate >= 75 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{rate}%</span>
                        ) : <span className="text-slate-300 text-xs">No data</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ── FEE COLLECTION TAB ──────────────────────────────────────────────── */
function FeeTab({ navigate }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/fees/summary')
      .then(r => setData(r.data || {}))
      .catch(() => toast.error('Failed to load fee data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton h="h-32" /><Skeleton h="h-48" /></div>;

  const totalInvoiced  = data?.total_invoiced  || 0;
  const totalCollected = data?.total_collected || 0;
  const outstanding    = data?.outstanding     || 0;
  const collectionRate = totalInvoiced > 0 ? Math.round((totalCollected / totalInvoiced) * 100) : 0;

  const pieData = [
    { name: 'Collected', value: totalCollected },
    { name: 'Outstanding', value: outstanding },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="fee-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Invoiced"   value={fmtNaira(totalInvoiced)}  sub="All fee invoices"        icon={DollarSign}    accent="#2563EB" testId="fee-invoiced" />
        <KpiCard label="Total Collected"  value={fmtNaira(totalCollected)} sub="Payments received"       icon={CheckCircle}   accent="#10B981" testId="fee-collected" />
        <KpiCard label="Outstanding"      value={fmtNaira(outstanding)}    sub="Yet to be paid"          icon={Clock}         accent="#EF4444" testId="fee-outstanding" />
        <KpiCard label="Collection Rate"  value={`${collectionRate}%`}     sub={`${data?.paid_count || 0} paid / ${data?.unpaid_count || 0} unpaid`} icon={TrendingUp} accent="#8B5CF6" testId="fee-rate" />
      </div>

      {/* Pie chart + table */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Collection vs Outstanding" sub="Visual breakdown" />
          {pieData.length === 0 ? (
            <EmptyChart icon={DollarSign} message="No fee invoices generated yet" />
          ) : (
            <div className="p-5">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={72} innerRadius={42} dataKey="value" paddingAngle={3}>
                    <Cell fill="#10B981" />
                    <Cell fill="#EF4444" />
                  </Pie>
                  <Tooltip formatter={(v) => fmtNaira(v)} />
                  <Legend formatter={(v) => <span className="text-xs text-slate-600">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title="Fee Summary" sub="Key collection metrics" />
          <div className="p-5 space-y-4">
            {[
              { label: 'Total Invoiced',  value: fmtNaira(totalInvoiced),  color: '#2563EB', pct: 100 },
              { label: 'Collected',       value: fmtNaira(totalCollected), color: '#10B981', pct: collectionRate },
              { label: 'Outstanding',     value: fmtNaira(outstanding),    color: '#EF4444', pct: 100 - collectionRate },
            ].map(row => (
              <div key={row.label}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-slate-600">{row.label}</span>
                  <span className="text-sm font-bold" style={{ color: row.color }}>{row.value}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full">
                  <div className="h-full rounded-full" style={{ width: `${row.pct}%`, backgroundColor: row.color }} />
                </div>
              </div>
            ))}

            <button onClick={() => navigate('/school/fees')}
              className="w-full flex items-center justify-center gap-2 mt-2 px-4 py-2.5 rounded-xl text-xs font-semibold text-white transition-colors"
              style={{ backgroundColor: '#2563EB' }}
              data-testid="view-fees-btn">
              View Full Fee Management <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ── REPORT CARDS TAB ────────────────────────────────────────────────── */
function ReportCardsTab({ navigate }) {
  return (
    <div className="space-y-6" data-testid="report-cards-tab">
      <Card>
        <CardHeader title="Bulk Report Cards" sub="Generate and print Nigerian-format report cards for any class" />
        <div className="p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#eef1ff] flex items-center justify-center mx-auto mb-4">
            <Printer className="w-8 h-8 text-[#2563EB]" strokeWidth={1.5} />
          </div>
          <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Bulk Report Cards</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto mb-6">
            Select a class and term to generate full Nigerian-standard report cards for all students at once.
            Includes principal remarks, grades, and print functionality.
          </p>
          <button
            onClick={() => navigate('/school/bulk-report')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-colors"
            style={{ backgroundColor: '#2563EB' }}
            data-testid="go-to-bulk-report-btn">
            <Printer className="w-4 h-4" />
            Open Bulk Report Cards
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Individual Report Card" sub="View a single student's report card" />
        <div className="p-6 text-center">
          <Award className="w-10 h-10 mx-auto mb-3" style={{ color: '#E2E8F0' }} strokeWidth={1.5} />
          <p className="text-sm text-slate-500">Access individual report cards from the Students section</p>
          <button
            onClick={() => navigate('/school/students')}
            className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 rounded-xl text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            data-testid="go-to-students-btn">
            Go to Students <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ── MAIN PAGE ───────────────────────────────────────────────────────── */
const TABS = [
  { id: 'overview',      label: 'Overview',     icon: BarChart2 },
  { id: 'academic',      label: 'Academic',     icon: TrendingUp },
  { id: 'attendance',    label: 'Attendance',   icon: CalendarCheck },
  { id: 'fees',          label: 'Fees',         icon: DollarSign },
  { id: 'report-cards',  label: 'Report Cards', icon: Printer },
];

export default function SchoolReportsPage() {
  const navigate = useNavigate();
  const [tab, setTab]           = useState('overview');
  const [dash, setDash]         = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [classes, setClasses]   = useState([]);
  const [terms, setTerms]       = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [dashRes, analyticsRes, clsRes, sesRes] = await Promise.all([
          api.get('/school/dashboard'),
          api.get('/school/analytics'),
          api.get('/academics/classes'),
          api.get('/academics/current-session'),
        ]);
        setDash(dashRes.data);
        setAnalytics(analyticsRes.data);
        setClasses(clsRes.data || []);
        setTerms(sesRes.data?.terms_for_year || []);
      } catch { toast.error('Failed to load report data'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="school-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 text-sm mt-0.5">School-wide analytics, academic performance, attendance and fee collection</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-full overflow-x-auto" data-testid="reports-tab-bar">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="space-y-4"><Skeleton h="h-32" /><Skeleton h="h-48" /><Skeleton h="h-48" /></div>
      ) : (
        <>
          {tab === 'overview'     && <OverviewTab dash={dash} analytics={analytics} />}
          {tab === 'academic'     && <AcademicTab classes={classes} terms={terms} />}
          {tab === 'attendance'   && <AttendanceTab dash={dash} />}
          {tab === 'fees'         && <FeeTab navigate={navigate} />}
          {tab === 'report-cards' && <ReportCardsTab navigate={navigate} />}
        </>
      )}
    </div>
  );
}
